import endpoints from './endpoints';
import {throttled, fetch} from '../../lib/fetch';

// Rental classes are static reference data, but three separate components each
// fetched them into their own state - ~13 identical requests per session. Cache
// the in-flight promise so concurrent callers share one request, and keep the
// resolved value for the lifetime of the app.
let rentalClassesPromise = null;

const rob = {
  get: async (service_request_id) => {
    const result = await fetch(
      `${endpoints.rob}/request/${service_request_id}`,
    );

    if (!result) {
      return null;
    }

    const json = await result.json();
    return json;
  },
  post: async (request) => {
    const result = await fetch(`${endpoints.rob}/request`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!result) {
      return null;
    }

    const json = await result.json();
    return json;
  },
  contract: async (license, request_id) => {
    const result = await fetch(`${endpoints.rob}/contract/${license}`, {
      method: 'POST',
      body: JSON.stringify({request_id}),
    });

    if (!result) {
      return null;
    }

    const json = await result.json();
    return json;
  },
  contract_by_dealer: async (license, dealer_id) => {
    const result = await fetch(`${endpoints.rob}/contract/${license}`, {
      method: 'POST',
      body: JSON.stringify({dealer_id}),
    });

    if (!result) {
      return null;
    }

    const json = await result.json();
    return json;
  },
  update_contract: async (license, request_id, dealer_id) => {
    const result = await fetch(`${endpoints.rob}/contract/${license}`, {
      method: 'POST',
      body: JSON.stringify({request_id, dealer_id, update: true}),
    });

    if (!result) {
      return null;
    }

    const json = await result.json();
    return json;
  },
  requests: async () => {
    const result = await fetch(`${endpoints.rob}/requests`, {
      method: 'POST',
    });

    if (!result) {
      return [];
    }

    const json = await result.json();
    return json;
  },
  delete_request: async (id) => {
    const result = await fetch(`${endpoints.rob}/request/${id}`, {
      method: 'DELETE',
    });

    if (!result) {
      return null;
    }

    const json = await result.json();
    return json;
  },
  component: async (license, request_id, rob_code) => {
    const result = await fetch(`${endpoints.rob}/components/${license}`, {
      method: 'POST',
      body: JSON.stringify({request_id, rob_codes: [rob_code]}),
    });

    if (!result) {
      return null;
    }

    const json = await result.json();
    return json;
  },
  all_components: async (request_id, rob_codes = null) => {
    const result = await fetch(`${endpoints.rob}/components`, {
      method: 'POST',
      body: JSON.stringify({request_id, rob_codes}),
    });

    if (!result) {
      return null;
    }

    const json = await result.json();
    return json;
  },
  components: async (license, request_id, rob_codes = null) => {
    const result = await fetch(`${endpoints.rob}/components/${license}`, {
      method: 'POST',
      body: JSON.stringify({request_id, rob_codes}),
    });

    if (!result) {
      return null;
    }

    const json = await result.json();
    return json;
  },
  damage_components: async (license, request_id, rob_codes = null) => {
    const result = await fetch(
      `${endpoints.rob}/damage_components/${license}`,
      {
        method: 'POST',
        body: JSON.stringify({request_id, rob_codes}),
      },
    );

    if (!result) {
      return null;
    }

    const json = await result.json();
    return json;
  },
  rental_classes: async () => {
    if (!rentalClassesPromise) {
      rentalClassesPromise = (async () => {
        const result = await fetch(`${endpoints.rob}/rentalclasses`);

        if (!result) {
          return null;
        }

        return await result.json();
      })().catch((e) => {
        // Never cache a failure: the next caller should retry.
        rentalClassesPromise = null;
        throw e;
      });
    }

    const json = await rentalClassesPromise;

    if (json === null) {
      // A null body is not worth keeping either.
      rentalClassesPromise = null;
    }

    return json;
  },

  request: async (request_id, partial_vin, current_mileage) => {
    const result = await fetch(`${endpoints.rob}/request/${request_id}`, {
      method: 'POST',
      body: JSON.stringify({
        partial_vin,
        current_mileage,
      }),
    });

    if (!result) {
      return null;
    }

    const json = await result.json();
    return json;
  },
  update_request: async (request_id) => {
    const result = await fetch(`${endpoints.rob}/request/${request_id}`, {
      method: 'PATCH',
    });

    if (!result) {
      return null;
    }

    const json = await result.json();
    return json;
  },
  invoice: async (request_id, invoiceNumber, discountInvoice) => {
    const result = await fetch(
      `${endpoints.rob}/request/${request_id}/invoice`,
      {
        method: 'POST',
        body: JSON.stringify({invoiceNumber, discountInvoice}),
      },
    );

    if (!result) {
      return {
        success: false,
      };
    }

    const json = await result.json();
    return json;
  },
  request_replacement_vehicle: async (request_id, replacement) => {
    const result = await fetch(
      `${endpoints.rob}/${request_id}/replacement_vehicle`,
      {
        method: 'POST',
        body: JSON.stringify({...replacement}),
      },
    );

    if (!result) {
      return {success: false};
    }

    const json = await result.json();
    return json;
  },
  replacement_vehicle: async (request_id) => {
    const result = await fetch(
      `${endpoints.rob}/${request_id}/replacement_vehicle`,
    );

    if (!result) {
      return {success: false};
    }

    const json = await result.json();
    return json;
  },
  cancel_replacement_vehicle: async (request_id) => {
    const result = await fetch(
      `${endpoints.rob}/${request_id}/replacement_vehicle`,
      {
        method: 'DELETE',
      },
    );

    if (!result) {
      return {success: false};
    }

    const json = await result.json();
    return json;
  },
  change_invoice_status: async (request_id, invoiceStatus) => {
    const result = await fetch(
      `${endpoints.rob}/request/${request_id}/invoice`,
      {
        method: 'PATCH',
        body: JSON.stringify({invoiceStatus}),
      },
    );

    if (!result) {
      return {
        success: false,
      };
    }

    const json = await result.json();
    return json;
  },
  activate_task: async (request_id, rob_id, task_id) => {
    const result = await fetch(
      `${endpoints.rob}/${rob_id}/component/${task_id}`,
      {
        method: 'PATCH',
        body: JSON.stringify({request_id}),
      },
    );

    if (!result) {
      return {
        success: false,
      };
    }

    const json = await result.json();
    return json;
  },
  fetch_tire: async (request_id, tire_id) => {
    const result = await fetch(`${endpoints.rob}/tire/${tire_id}`, {
      method: 'POST',
      body: JSON.stringify({request_id}),
    });

    if (!result) {
      return {
        success: false,
      };
    }

    const json = await result.json();
    return json;
  },
  lessors: async () => {
    const result = await fetch(`${endpoints.rob}/lessors`);

    if (!result) {
      return null;
    }

    const json = await result.json();
    return json;
  },
  task_price_check: async (...args) => {
    const payload =
      args.length === 1 && args[0] && typeof args[0] === 'object'
        ? args[0]
        : {
            company_id: args[0],
            lessor: args[1],
            rob_code: args[2],
            operation_code: args[3],
            vin_number: args[4],
            hourly_rate: args[5],
            request_id: args[6],
            license: args[7],
          };

    const result = await fetch(`${endpoints.rob}/component/price`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (!result) {
      return {
        success: false,
      };
    }

    const json = await result.json();
    return json;
  },
  articles: async (dealer_id, rob_code, item_mp_id) => {
    const result = await fetch(`${endpoints.rob}/articles`, {
      method: 'POST',
      body: JSON.stringify({dealer_id, rob_code, item_mp_id}),
    });

    if (!result) {
      return {
        success: false,
      };
    }

    const json = await result.json();
    return json;
  },
  invoice_pdf: async (request_id) => {
    const result = await fetch(`${endpoints.rob}/${request_id}/invoice/pdf`);

    if (!result) {
      return {
        success: false,
      };
    }

    const blob = await result.blob();
    return new Blob([blob], {type: 'application/pdf'});
  },
  history: throttled(async (signal, dealer_id, license) => {
    const result = await fetch(
      `${endpoints.rob}/history/${dealer_id}/${license}`,
      {signal},
    );

    if (!result) {
      return {
        success: false,
      };
    }

    const json = await result.json();
    return json;
  }),
};

// Not part of the `rob` API surface: exposed for tests and for sign-out, where
// cached reference data should be dropped.
export const resetRentalClassesCache = () => {
  rentalClassesPromise = null;
};

export default rob;
